/**
 * Состояние карты в адресе страницы (ссылка открывает то же самое) и в
 * localStorage (при открытии без параметров - то, что было в прошлый раз).
 *
 *   ?ll=60.6057,56.8389&z=13.5&b=20&p=45              вид карты (lon,lat как у Яндекса)
 *   &mode=route                                        вкладка "Маршрут"
 *   &rtext=56.84,60.61~~56.91,60.81                    точки маршрута; пустая - между ~~
 *   &rn=Откуда~~Куда                                   подписи точек в полях
 *   &rt=auto&h=4&w=40&ax=10&bd=FORBID&nt=1&nf=1        профиль, грузовик, границы, без платных/паромов
 *   &pt=56.7731,61.0192&pk=b                           открытая подсказка (pk: b - дом, s - улица)
 */

export type MapMode = 'search' | 'route';

export interface MapViewState {
  readonly lat: number;
  readonly lon: number;
  readonly zoom: number;
  readonly bearing: number;
  readonly pitch: number;
}

export interface RouteUrlPoint {
  readonly lat: number;
  readonly lon: number;
  readonly label: string;
}

export interface RouteUrlState {
  /** null - поле пустое. */
  readonly points: readonly (RouteUrlPoint | null)[];
  readonly profile: 'truck' | 'auto';
  readonly height: string;
  readonly weight: string;
  readonly axleLoad: string;
  readonly borders: 'ALLOW' | 'AVOID' | 'FORBID';
  readonly avoidTolls: boolean;
  readonly avoidFerries: boolean;
}

export interface UrlState {
  readonly view?: MapViewState;
  readonly mode?: MapMode;
  readonly route?: RouteUrlState;
  /** Открытая подсказка: точка и что под ней было (дом/улица/другое). */
  readonly place?: { readonly lat: number; readonly lon: number; readonly kind: 'building' | 'street' | 'other' };
}

const STORAGE_KEY = 'gar-map:state';
const BORDERS = ['ALLOW', 'AVOID', 'FORBID'] as const;

const num = (value: string | null): number | null => {
  if (value == null || value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const pair = (value: string | undefined): [number, number] | null => {
  const [a, b] = (value ?? '').split(',').map(num);
  return a != null && b != null ? [a, b] : null;
};

const round = (value: number, digits: number) => Number(value.toFixed(digits));

export function parseState(search: string): UrlState {
  const params = new URLSearchParams(search);
  const state: { -readonly [K in keyof UrlState]: UrlState[K] } = {};

  const ll = pair(params.get('ll') ?? undefined);
  const zoom = num(params.get('z'));
  if (ll && Math.abs(ll[1]) <= 90 && Math.abs(ll[0]) <= 180) {
    state.view = {
      lon: ll[0],
      lat: ll[1],
      zoom: zoom != null ? Math.min(22, Math.max(0, zoom)) : 13,
      bearing: num(params.get('b')) ?? 0,
      pitch: Math.min(85, Math.max(0, num(params.get('p')) ?? 0)),
    };
  }

  const mode = params.get('mode');
  if (mode === 'route' || mode === 'search') state.mode = mode;

  const rtext = params.get('rtext');
  if (rtext != null) {
    const labels = (params.get('rn') ?? '').split('~');
    const points = rtext.split('~').map((text, i) => {
      const point = pair(text);
      return point ? { lat: point[0], lon: point[1], label: labels[i] || text } : null;
    });
    const rt = params.get('rt');
    const bd = params.get('bd');
    state.route = {
      points: points.length >= 2 ? points.slice(0, 10) : [points[0] ?? null, null],
      profile: rt === 'auto' ? 'auto' : 'truck',
      height: params.get('h') ?? '',
      weight: params.get('w') ?? '',
      axleLoad: params.get('ax') ?? '',
      borders: (BORDERS as readonly string[]).includes(bd ?? '') ? (bd as RouteUrlState['borders']) : 'AVOID',
      avoidTolls: params.get('nt') === '1',
      avoidFerries: params.get('nf') === '1',
    };
  }

  const pt = pair(params.get('pt') ?? undefined);
  if (pt) {
    const pk = params.get('pk');
    state.place = { lat: pt[0], lon: pt[1], kind: pk === 'b' ? 'building' : pk === 's' ? 'street' : 'other' };
  }
  return state;
}

export function serializeState(state: UrlState): string {
  const params = new URLSearchParams();
  if (state.view) {
    const { lat, lon, zoom, bearing, pitch } = state.view;
    params.set('ll', `${round(lon, 6)},${round(lat, 6)}`);
    params.set('z', String(round(zoom, 2)));
    if (Math.round(bearing) !== 0) params.set('b', String(Math.round(bearing)));
    if (Math.round(pitch) !== 0) params.set('p', String(Math.round(pitch)));
  }
  if (state.mode === 'route') params.set('mode', 'route');
  if (state.mode === 'route' && state.route) {
    const { points, profile, height, weight, axleLoad, borders, avoidTolls, avoidFerries } = state.route;
    if (points.some(Boolean)) {
      params.set('rtext', points.map(p => (p ? `${round(p.lat, 6)},${round(p.lon, 6)}` : '')).join('~'));
      params.set('rn', points.map(p => (p ? p.label.replace(/~/g, '-') : '')).join('~'));
    }
    if (profile === 'auto') params.set('rt', 'auto');
    if (profile === 'truck') {
      if (height) params.set('h', height);
      if (weight) params.set('w', weight);
      if (axleLoad) params.set('ax', axleLoad);
    }
    if (borders !== 'AVOID') params.set('bd', borders);
    if (avoidTolls) params.set('nt', '1');
    if (avoidFerries) params.set('nf', '1');
  }
  if (state.place && state.mode !== 'route') {
    params.set('pt', `${round(state.place.lat, 6)},${round(state.place.lon, 6)}`);
    if (state.place.kind !== 'other') params.set('pk', state.place.kind === 'building' ? 'b' : 's');
  }
  // запятые и тильды - читаемыми, как у Яндекса
  return params.toString().replace(/%2C/g, ',').replace(/%7E/g, '~');
}

/** Состояние при открытии: из адреса, а если в нём ничего нет - сохранённое. */
export function initialState(): UrlState {
  const fromUrl = parseState(window.location.search);
  if (Object.keys(fromUrl).length > 0) return fromUrl;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved ? parseState(saved) : {};
  } catch {
    return {}; // localStorage недоступен (приватный режим и т.п.)
  }
}

/** Пишет состояние в адрес (без новой записи в истории) и в localStorage. */
export function saveState(state: UrlState): void {
  const query = serializeState(state);
  const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  if (url !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
    window.history.replaceState(null, '', url);
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, query);
  } catch {
    // не сохранилось - при следующем открытии просто начнём с начала
  }
}
